import { useState, useEffect } from 'preact/hooks';
import { getUsers, getCalendarData, getHolidays, getLeaveTypes } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FeatureLocked } from '../components/FeatureLocked';
import { useI18n } from '../i18n/I18nContext';
import { getPrivacyAwareLeaveColor, getPrivacyAwareLeaveTypeName } from '../lib/leave-privacy';

export function WallchartPage({ user, workspace }) {
  const { hasFeature } = useAuth();
  const { t } = useI18n();

  // Check for planner (wallchart) feature access
  if (!hasFeature('wallchart')) {
    return (
      <FeatureLocked
        feature={t('planner.feature_name')}
        description={t('planner.feature_description')}
      />
    );
  }

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  });
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveTypesList, setLeaveTypesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showPending, setShowPending] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDepartments, setExportDepartments] = useState([]);
  const [exportFormat, setExportFormat] = useState('buddy');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  // Format date as YYYY-MM-DD in local timezone (avoids UTC shift issues)
  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async function loadData() {
    try {
      setLoading(true);
      const startStr = formatDateLocal(startDate);
      const endStr = formatDateLocal(endDate);

      const [usersData, calendarData, holidaysData, leaveTypesData] = await Promise.all([
        getUsers(),
        getCalendarData(startStr, endStr),
        getHolidays(startDate.getFullYear()),
        getLeaveTypes()
      ]);

      setUsers(usersData || []);
      setLeaves(calendarData.leaves || []);
      setHolidays(holidaysData || []);
      setLeaveTypesList(leaveTypesData || []);
    } catch (error) {
      console.error('Failed to load wallchart data:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDatesArray() {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  function navigate(direction) {
    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);
    const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    newStart.setDate(newStart.getDate() + (direction * daysDiff));
    newEnd.setDate(newEnd.getDate() + (direction * daysDiff));

    setStartDate(newStart);
    setEndDate(newEnd);
  }

  // Get ALL leaves for a user on a specific date (there can be multiple)
  function getUserLeavesForDate(userId, dateStr) {
    const validStatuses = showPending ? ['approved', 'pending'] : ['approved'];
    return leaves.filter(leave =>
      leave.user_id === userId &&
      dateStr >= leave.start_date &&
      dateStr <= leave.end_date &&
      validStatuses.includes(leave.status)
    );
  }

  // Calculate total hours for a user on a specific date (aggregates multiple leaves)
  function getUserLeaveHoursForDate(userId, dateStr) {
    // Don't show leave on weekends - leave is only for working days
    const currentDate = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { leaves: [], totalHours: 0, hasLeave: false };
    }

    const userLeaves = getUserLeavesForDate(userId, dateStr);
    if (userLeaves.length === 0) return { leaves: [], totalHours: 0, hasLeave: false };

    let totalHours = 0;
    for (const leave of userLeaves) {
      const isHoursBased = leave.leave_type_unit === 'hours';
      if (isHoursBased && leave.duration_days) {
        // For hours-based leave spanning multiple days, calculate hours per WORKING day
        const startD = new Date(leave.start_date + 'T12:00:00');
        const endD = new Date(leave.end_date + 'T12:00:00');

        // Count only working days (Mon-Fri), excluding weekends
        let workingDays = 0;
        const current = new Date(startD);
        while (current <= endD) {
          const dow = current.getDay();
          if (dow !== 0 && dow !== 6) {
            workingDays++;
          }
          current.setDate(current.getDate() + 1);
        }

        // Divide total hours by working days, not calendar days
        totalHours += workingDays > 0 ? leave.duration_days / workingDays : leave.duration_days;
      } else if (leave.duration_days && leave.duration_days < 1) {
        // Partial day (half-day)
        totalHours += leave.duration_days * 8;
      } else {
        // Full day = 8 hours
        totalHours += 8;
      }
    }

    return { leaves: userLeaves, totalHours, hasLeave: true };
  }

  // Legacy function for backward compatibility - returns first leave for styling
  function getUserLeaveForDate(userId, dateStr) {
    const validStatuses = showPending ? ['approved', 'pending'] : ['approved'];
    return leaves.find(leave =>
      leave.user_id === userId &&
      dateStr >= leave.start_date &&
      dateStr <= leave.end_date &&
      validStatuses.includes(leave.status)
    );
  }

  function isHoliday(dateStr) {
    return holidays.find(h => h.date === dateStr);
  }

  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  function isToday(date) {
    const today = new Date();
    return formatDateLocal(date) === formatDateLocal(today);
  }

  // Helper to determine if text should be white or black based on background
  function getContrastText(hexColor) {
    if (!hexColor) return '#000000';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  // Default colors for leave types without a color set
  const defaultColors = {
    'Vacation': '#8B5CF6',
    'Vacation Leave': '#8B5CF6',
    'Annual Leave': '#8B5CF6',
    'Sick': '#EF4444',
    'Sick Leave': '#EF4444',
    'Sick day': '#EF4444',
    'Maternity': '#EC4899',
    'Maternity Leave': '#EC4899',
    'Paternity': '#EC4899',
    'Paternity Leave': '#EC4899'
  };

  function getLeaveColorDefault(leaveTypeName) {
    // First check if we have the leave type in our list with a color
    const leaveType = leaveTypesList.find(lt => lt.name === leaveTypeName);
    if (leaveType?.color) {
      return leaveType.color;
    }
    // Fall back to default colors or gray
    return defaultColors[leaveTypeName] || '#6B7280';
  }

  // Privacy-aware leave color - hides paid/unpaid distinction from non-admins
  function getLeaveColor(leaveTypeName, leaveUserId) {
    const actualColor = getLeaveColorDefault(leaveTypeName);
    const isOwnLeave = leaveUserId === user?.id;
    return getPrivacyAwareLeaveColor(leaveTypeName, actualColor, user?.role, isOwnLeave);
  }

  // Privacy-aware leave type name - hides (Full Pay)/(Half Pay)/(Unpaid) from non-admins
  function getLeaveTypeName(leaveTypeName, leaveUserId) {
    const isOwnLeave = leaveUserId === user?.id;
    return getPrivacyAwareLeaveTypeName(leaveTypeName, user?.role, isOwnLeave);
  }

  function isPartialDay(leave) {
    const isHoursBased = leave.leave_type_unit === 'hours';
    const hasTimes = leave.start_time && leave.end_time;
    const isPartialDuration = leave.duration_days && leave.duration_days < 1;
    return isHoursBased || hasTimes || isPartialDuration;
  }


  // Export functions
  function openExportModal() {
    const allDeptIds = departments.map(d => d.id);
    setExportDepartments(allDeptIds);
    setExportStartDate(formatDateLocal(startDate));
    setExportEndDate(formatDateLocal(endDate));
    setShowExportModal(true);
  }

  function toggleDepartment(deptId) {
    setExportDepartments(prev =>
      prev.includes(deptId) ? prev.filter(id => id !== deptId) : [...prev, deptId]
    );
  }

  function selectAllDepartments() {
    setExportDepartments(departments.map(d => d.id));
  }

  function clearDepartments() {
    setExportDepartments([]);
  }

  function exportLeaveData() {
    const usersToExport = users.filter(u => {
      if (exportDepartments.length === 0) return true;
      return exportDepartments.includes(u.department_id);
    });

    const userLeaves = {};
    usersToExport.forEach(u => {
      userLeaves[u.id] = {
        id: u.slack_id || u.id,
        name: u.name,
        department: u.department_name || '',
        leaves: []
      };
    });

    leaves.forEach(leave => {
      if (!userLeaves[leave.user_id]) return;
      if (leave.status !== 'approved' && leave.status !== 'pending') return;

      const leaveStart = leave.start_date > exportStartDate ? leave.start_date : exportStartDate;
      const leaveEnd = leave.end_date < exportEndDate ? leave.end_date : exportEndDate;

      if (leaveStart > exportEndDate || leaveEnd < exportStartDate) return;

      const unit = leave.leave_type_unit || 'days';
      let totalHours = unit === 'hours' ? (leave.duration_days || 0) : (leave.duration_days || 1) * 8;

      const leaveDates = [];
      const current = new Date(leaveStart);
      const end = new Date(leaveEnd);
      while (current <= end) {
        const dateStr = formatDateLocal(current);
        const dayOfWeek = current.getDay();
        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
        const isHolidayDay = holidays.find(h => h.date === dateStr);
        if (!isWeekendDay && !isHolidayDay) {
          leaveDates.push(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      const workingDays = leaveDates.length;
      const hoursPerWorkingDay = workingDays > 0 ? totalHours / workingDays : totalHours;

      userLeaves[leave.user_id].leaves.push({
        leaveType: leave.leave_type,
        status: leave.status,
        startDate: leaveStart,
        endDate: leaveEnd,
        totalHours,
        dates: leaveDates,
        hoursPerDay: hoursPerWorkingDay,
        createdAt: leave.created_at || exportStartDate
      });
    });

    let csvContent, filename;

    if (exportFormat === 'buddy') {
      const csvRows = [];
      csvRows.push(['Name', 'Leave Type', 'Status', 'Date From', 'Date To', 'Application Date', 'Hours', 'Already Paid', 'Statutory', 'Occupational'].join(','));

      Object.values(userLeaves).forEach(userData => {
        if (userData.leaves.length === 0) return;
        userData.leaves.forEach(leave => {
          const formatDate = (dateStr) => {
            const [y, m, d] = dateStr.split('-');
            return `${d}/${m}/${y}`;
          };
          const isSick = leave.leaveType?.toLowerCase().includes('sick');
          const isMaternity = leave.leaveType?.toLowerCase().includes('maternity');
          const statutory = (isSick || isMaternity) ? 'Yes' : '';

          csvRows.push([
            `"${userData.name}"`,
            `"${leave.leaveType}"`,
            leave.status === 'approved' ? 'Approved' : 'Pending',
            formatDate(leave.startDate),
            formatDate(leave.endDate),
            formatDate(leave.createdAt?.split('T')[0] || leave.startDate),
            leave.totalHours.toFixed(1),
            '',
            statutory,
            ''
          ].join(','));
        });
      });

      csvContent = csvRows.join('\n');
      filename = `buddy-leave-import-${exportStartDate}-to-${exportEndDate}.csv`;
    } else {
      const csvRows = [];
      csvRows.push(['Report Period', `${exportStartDate} to ${exportEndDate}`].join(','));
      csvRows.push(['Generated', new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })].join(','));
      csvRows.push('');
      csvRows.push(['Name', 'Department', 'Leave Type', 'Status', 'From Date', 'To Date', 'Total Hours'].join(','));

      Object.values(userLeaves).forEach(userData => {
        if (userData.leaves.length === 0) return;
        userData.leaves.forEach(leave => {
          csvRows.push([
            `"${userData.name}"`,
            `"${userData.department}"`,
            `"${leave.leaveType}"`,
            leave.status,
            leave.startDate,
            leave.endDate,
            leave.totalHours.toFixed(1)
          ].join(','));
        });
      });

      csvContent = csvRows.join('\n');
      filename = `leave-report-${exportStartDate}-to-${exportEndDate}.csv`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  }

  // Filter users
  const filteredUsers = users.filter(u => {
    if (departmentFilter !== 'all' && u.department_id !== departmentFilter) return false;
    if (locationFilter !== 'all' && u.location_id !== locationFilter) return false;
    return true;
  });

  // Get unique departments and locations
  const departments = users
    .filter(u => u.department_id && u.department_name)
    .reduce((acc, u) => {
      if (!acc.find(d => d.id === u.department_id)) {
        acc.push({ id: u.department_id, name: u.department_name });
      }
      return acc;
    }, []);

  const locations = users
    .filter(u => u.location_id && u.location_name)
    .reduce((acc, u) => {
      if (!acc.find(l => l.id === u.location_id)) {
        acc.push({ id: u.location_id, name: u.location_name });
      }
      return acc;
    }, []);

  const dates = getDatesArray();
  const dayAbbrevs = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <div class="wallchart-page">
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">{t('planner.page_title')}</h1>
          <p class="bento-page-subtitle">{t('planner.page_subtitle')}</p>
        </div>
      </div>

      {/* Controls */}
      <div class="bento-card bento-controls-card" style={{ marginBottom: 'var(--bento-gap)' }}>
        <div class="wallchart-nav">
          <input
            type="date"
            class="date-input"
            value={formatDateLocal(startDate)}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split('-').map(Number);
              setStartDate(new Date(y, m - 1, d));
            }}
          />
          <span class="date-separator">—</span>
          <input
            type="date"
            class="date-input"
            value={formatDateLocal(endDate)}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split('-').map(Number);
              setEndDate(new Date(y, m - 1, d));
            }}
          />
          <button class="nav-btn" onClick={() => navigate(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button class="nav-btn" onClick={() => navigate(1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <div class="wallchart-filters">
          <label class="toggle-label">
            <input
              type="checkbox"
              checked={showPending}
              onChange={(e) => setShowPending(e.target.checked)}
            />
            <span class="toggle-slider"></span>
            {t('planner.filter.pending_request')}
          </label>

          <span class="filter-label">{t('planner.filter.filter_by')}</span>

          {departments.length > 0 && (
            <select
              class="filter-select"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">{t('planner.filter.all_departments')}</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}

          {locations.length > 0 && (
            <select
              class="filter-select"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">{t('planner.filter.all_locations')}</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}

          <button class="btn btn-sm export-btn" onClick={openExportModal}>
            {t('planner.export.button')}
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div class="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div class="modal export-modal" onClick={(e) => e.stopPropagation()}>
            <div class="modal-header">
              <h3>{t('planner.export.title')}</h3>
              <button class="modal-close" onClick={() => setShowExportModal(false)}>&times;</button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label>{t('planner.export.format')}</label>
                <div class="format-options">
                  <label class="radio-label">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="buddy"
                      checked={exportFormat === 'buddy'}
                      onChange={() => setExportFormat('buddy')}
                    />
                    <span>{t('planner.export.buddy_payroll')}</span>
                  </label>
                  <label class="radio-label">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="generic"
                      checked={exportFormat === 'generic'}
                      onChange={() => setExportFormat('generic')}
                    />
                    <span>{t('planner.export.generic_csv')}</span>
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label>{t('planner.export.date_range')}</label>
                <div class="date-range-inputs">
                  <input
                    type="date"
                    class="form-input"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                  />
                  <span>{t('common.to')}</span>
                  <input
                    type="date"
                    class="form-input"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                  />
                </div>
              </div>

              {departments.length > 0 && (
                <div class="form-group">
                  <label>
                    {t('planner.export.departments')}
                    <span class="dept-actions">
                      <button type="button" class="btn-link" onClick={selectAllDepartments}>{t('common.all')}</button>
                      <button type="button" class="btn-link" onClick={clearDepartments}>{t('common.none')}</button>
                    </span>
                  </label>
                  <div class="department-checkboxes">
                    {departments.map(d => (
                      <label key={d.id} class="checkbox-label">
                        <input
                          type="checkbox"
                          checked={exportDepartments.includes(d.id)}
                          onChange={() => toggleDepartment(d.id)}
                        />
                        <span>{d.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" onClick={() => setShowExportModal(false)}>{t('common.cancel')}</button>
              <button class="btn btn-primary" onClick={exportLeaveData}>{t('planner.export.button')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Wallchart Grid */}
      <div class="bento-card bento-table-card" style={{ overflow: 'hidden' }}>
        <table class="wallchart-table" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th class="user-col"></th>
              {dates.map(date => {
                const dateStr = formatDateLocal(date);
                const weekend = isWeekend(date);
                const holiday = isHoliday(dateStr);
                const today = isToday(date);

                return (
                  <th
                    key={dateStr}
                    class={`date-col ${weekend ? 'weekend' : ''} ${holiday ? 'holiday' : ''} ${today ? 'today' : ''}`}
                    title={holiday ? holiday.name : undefined}
                  >
                    <div class="date-header">
                      <span class="date-day">{dayAbbrevs[date.getDay()]}</span>
                      <span class="date-num">{date.getDate()}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map(u => (
                <tr key={u.id}>
                  <td class="user-col">
                    <div class="user-info">
                      <div class="avatar">
                        {u.avatar_url ? <img src={u.avatar_url} alt="" /> : u.name?.charAt(0)}
                      </div>
                      <div class="user-details">
                        <span class="user-name">{u.name}</span>
                      </div>
                    </div>
                  </td>
                  {dates.map(date => {
                    const dateStr = formatDateLocal(date);
                    const { leaves: userLeaves, totalHours, hasLeave } = getUserLeaveHoursForDate(u.id, dateStr);
                    const leave = userLeaves[0]; // Use first leave for styling/color
                    const holiday = isHoliday(dateStr);
                    const weekend = isWeekend(date);
                    const today = isToday(date);

                    let cellClass = 'day-cell';
                    let cellStyle = {};

                    if (weekend) cellClass += ' weekend';
                    if (today) cellClass += ' today';
                    if (holiday) cellClass += ' holiday';

                    // Display aggregated hours for this day
                    let hoursDisplay = '';
                    if (hasLeave) {
                      const color = getLeaveColor(leave.leave_type, u.id);
                      // For partial day leaves (less than 8h), show time range if available
                      if (totalHours < 8 && userLeaves.length === 1 && userLeaves[0].start_time && userLeaves[0].end_time) {
                        // Format times: "09:00" -> "9" or "09:30" -> "9:30"
                        const formatTime = (t) => {
                          const [h, m] = t.split(':');
                          return m === '00' ? parseInt(h) : `${parseInt(h)}:${m}`;
                        };
                        hoursDisplay = `${formatTime(userLeaves[0].start_time)}-${formatTime(userLeaves[0].end_time)}`;
                      } else {
                        hoursDisplay = totalHours >= 1 ? Math.round(totalHours) + 'h' : '';
                      }

                      // Check if any leave is pending
                      const hasPending = userLeaves.some(l => l.status === 'pending');
                      const allPending = userLeaves.every(l => l.status === 'pending');

                      if (allPending) {
                        cellClass += ' pending';
                        cellStyle.borderColor = color;
                        cellStyle.color = color;
                      } else {
                        cellClass += ' leave';
                        cellStyle.backgroundColor = color;
                        cellStyle.color = getContrastText(color);
                      }
                    }

                    // Build tooltip with all leaves for this date
                    let tooltip = '';
                    if (hasLeave) {
                      tooltip = userLeaves.map(l => `${getLeaveTypeName(l.leave_type, u.id)} - ${l.status}`).join(', ');
                      if (hoursDisplay) tooltip += ` (${hoursDisplay} total)`;
                    } else if (holiday) {
                      tooltip = holiday.name;
                    }

                    return (
                      <td
                        key={dateStr}
                        class={cellClass}
                        style={cellStyle}
                        title={tooltip}
                      >
                        {hasLeave ? hoursDisplay : ''}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colspan={dates.length + 1} class="empty-state">
                  {t('planner.empty_state')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Legend - privacy-aware: non-admins see simplified names, deduplicated */}
        <div class="wallchart-legend">
          {(() => {
            const seen = new Set();
            return leaveTypesList.map(lt => {
              const displayName = getLeaveTypeName(lt.name, null); // null = not own leave, will use privacy rules
              // Skip duplicates (e.g., "Sick Leave (Full Pay)" and "Sick Leave (Half Pay)" both become "Sick Leave")
              if (seen.has(displayName)) return null;
              seen.add(displayName);
              const bgColor = lt.color || defaultColors[lt.name] || '#6B7280';
              return (
                <span
                  key={lt.id}
                  class="legend-item"
                  style={{ backgroundColor: bgColor, color: getContrastText(bgColor) }}
                >
                  {displayName}
                </span>
              );
            }).filter(Boolean);
          })()}
          <span class="legend-item holiday-badge">{t('planner.legend.holidays')}</span>
          <span class="legend-item non-working">{t('planner.legend.non_working')}</span>
          <span class="legend-item partial-badge">{t('planner.legend.partial_day')}</span>
          <span class="legend-item pending-badge">{t('planner.legend.pending')}</span>
        </div>
      </div>

      <style>{`
        .wallchart-page {
          /* Removed padding - container handles it */
        }

        .bento-controls-card {
          padding: 1rem 1.25rem;
        }

        .wallchart-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .wallchart-nav {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .date-input {
          padding: 0.375rem 0.5rem;
          border: 1px solid #D1D5DB;
          border-radius: 4px;
          font-size: 0.875rem;
          font-family: inherit;
          color: #374151;
        }

        .date-separator {
          color: #9CA3AF;
        }

        .nav-btn {
          width: 28px;
          height: 28px;
          border: 1px solid #D1D5DB;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          color: #6B7280;
        }

        .nav-btn:hover {
          background: #F3F4F6;
        }

        .wallchart-filters {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
        }

        .toggle-label input {
          width: 36px;
          height: 20px;
          appearance: none;
          background: #D1D5DB;
          border-radius: 10px;
          position: relative;
          cursor: pointer;
          transition: background 0.2s;
        }

        .toggle-label input:checked {
          background: #8B5CF6;
        }

        .toggle-label input::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          transition: transform 0.2s;
        }

        .toggle-label input:checked::after {
          transform: translateX(16px);
        }

        .filter-label {
          font-size: 0.875rem;
          color: #6B7280;
        }

        .filter-select {
          padding: 0.375rem 0.75rem;
          border: 1px solid #D1D5DB;
          border-radius: 4px;
          font-size: 0.875rem;
          background: white;
          min-width: 140px;
        }

        .export-btn {
          padding: 0.375rem 0.75rem;
          background: white;
          border: 1px solid #D1D5DB;
          border-radius: 4px;
          font-size: 0.875rem;
          cursor: pointer;
        }

        .export-btn:hover {
          background: #F3F4F6;
        }

        .bento-table-card {
          overflow-x: auto;
        }

        .wallchart-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .wallchart-table th,
        .wallchart-table td {
          text-align: center;
          border: 1px solid #E5E7EB;
        }

        .wallchart-table th {
          padding: 0.5rem 0.25rem;
          font-weight: 600;
          color: #6B7280;
          background: #F9FAFB;
          font-size: 0.8125rem;
        }

        .user-col {
          text-align: left !important;
          padding: 0.5rem 0.75rem !important;
          width: 120px;
          min-width: 120px;
          max-width: 120px;
          position: sticky;
          left: 0;
          background: white;
          z-index: 1;
        }

        thead .user-col {
          background: #F9FAFB;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #E5E7EB;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 600;
          color: #6B7280;
          flex-shrink: 0;
          overflow: hidden;
        }

        .avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-details {
          overflow: hidden;
        }

        .user-name {
          font-weight: 500;
          color: #8B5CF6;
          font-size: 0.8125rem;
          line-height: 1.2;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .date-col {
          width: 40px;
          min-width: 40px;
        }

        .date-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }

        .date-day {
          font-size: 0.6875rem;
          text-transform: uppercase;
          color: #9CA3AF;
          font-weight: 500;
        }

        .date-num {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .date-col.weekend .date-num,
        .date-col.weekend .date-day {
          color: #D1D5DB;
        }

        .date-col.holiday .date-num {
          color: #92400E;
        }

        .date-col.today .date-num {
          background: #3B82F6;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .day-cell {
          padding: 0.25rem;
          font-weight: 600;
          color: #374151;
          height: 32px;
          transition: all 0.15s;
          font-size: 0.6875rem;
        }

        .day-cell.weekend {
          background: #F9FAFB;
          color: #9CA3AF;
        }

        .day-cell.holiday {
          background: #FEF3C7;
          color: #92400E;
        }

        .day-cell.today {
          box-shadow: inset 0 0 0 2px #3B82F6;
        }

        .day-cell.leave {
          color: white;
          font-weight: 600;
        }

        .day-cell.pending {
          background: white;
          border: 2px solid;
          font-weight: 600;
        }

        .day-cell.partial {
          color: white;
          font-weight: 600;
        }

        .empty-state {
          padding: 2rem;
          text-align: center;
          color: #6B7280;
        }

        /* Legend */
        .wallchart-legend {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem;
          padding: 1rem 0.75rem;
          border-top: 1px solid #E5E7EB;
          background: white;
        }

        .legend-item {
          padding: 0.625rem 1.25rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
        }

        .legend-item.holiday-badge {
          background: #F59E0B;
          color: #FFFFFF;
        }

        .legend-item.non-working {
          background: #E5E7EB;
          color: #374151;
        }

        .legend-item.partial-badge {
          background: #C4B5FD;
          color: #374151;
        }

        .legend-item.pending-badge {
          background: #FFF;
          color: #F97316;
          border: 2px dashed #F97316;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: white;
          border-radius: 8px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #E5E7EB;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6B7280;
        }

        .modal-body {
          padding: 1rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          padding: 1rem;
          border-top: 1px solid #E5E7EB;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .format-options {
          display: flex;
          gap: 1rem;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .date-range-inputs {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .date-range-inputs input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #D1D5DB;
          border-radius: 4px;
          font-family: inherit;
          font-size: 0.875rem;
        }

        .dept-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-link {
          background: none;
          border: none;
          color: #8B5CF6;
          cursor: pointer;
          font-size: 0.75rem;
        }

        .department-checkboxes {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border-radius: 4px;
          font-size: 0.875rem;
          cursor: pointer;
          border: none;
        }

        .btn-primary {
          background: #8B5CF6;
          color: white;
        }

        .btn-secondary {
          background: #F3F4F6;
          color: #374151;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .wallchart-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .wallchart-nav {
            justify-content: center;
            flex-wrap: wrap;
          }

          .wallchart-filters {
            flex-wrap: wrap;
            justify-content: center;
          }

          .filter-label {
            display: none;
          }

          .date-input {
            max-width: 130px;
          }
        }

        @media (max-width: 480px) {
          .bento-controls-card {
            padding: 0.75rem !important;
          }

          .wallchart-nav {
            gap: 0.25rem;
          }

          .date-input {
            padding: 0.25rem 0.375rem;
            font-size: 0.75rem;
            max-width: 115px;
          }

          .date-separator {
            margin: 0 0.125rem;
          }

          .nav-btn {
            width: 24px;
            height: 24px;
          }

          .filter-select {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
            min-width: 90px;
          }

          .export-btn {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
          }

          .toggle-label {
            font-size: 0.75rem;
          }

          .toggle-label input {
            width: 30px;
            height: 16px;
          }

          .toggle-label input::after {
            width: 12px;
            height: 12px;
          }

          .toggle-label input:checked::after {
            transform: translateX(14px);
          }

          /* Narrower user column on mobile */
          .user-col {
            width: 80px !important;
            min-width: 80px !important;
            max-width: 80px !important;
            padding: 0.25rem 0.5rem !important;
          }

          .user-info {
            gap: 0.25rem;
          }

          .avatar {
            width: 22px !important;
            height: 22px !important;
            font-size: 0.5rem !important;
          }

          .user-name {
            font-size: 0.625rem !important;
            -webkit-line-clamp: 1 !important;
          }

          .date-col {
            width: 32px !important;
            min-width: 32px !important;
          }

          .date-header .date-day {
            font-size: 0.5625rem !important;
          }

          .date-header .date-num {
            font-size: 0.75rem !important;
          }

          .day-cell {
            font-size: 0.5rem !important;
            padding: 0.125rem !important;
            height: 28px !important;
          }

          /* Legend on mobile */
          .wallchart-legend {
            padding: 0.5rem !important;
            gap: 0.5rem !important;
          }

          .legend-item {
            font-size: 0.6875rem !important;
            padding: 0.375rem 0.625rem !important;
            border-radius: 6px !important;
          }
        }
      `}</style>
    </div>
  );
}
